import {
  applyCashSessionViaRpc,
  completeCashSessionAfterServerApply,
} from '../../cash/infra/cashSessionCommandRpc';
import { buildCatalogSyncOperationId } from './catalogSyncOperationId';
import { extractOperationId } from './ingestDeviceCommands';
import type { FlushRpcAdapter } from './rpcHelpers';
import type { SyncEntity, SyncOperation, SyncQueueItem } from './syncTypes';

export type CommandPort = {
  prepareOnlinePayload: (
    operation: SyncOperation,
    payload: Record<string, unknown>,
  ) => Record<string, unknown>;
  applyViaRpc: FlushRpcAdapter;
  afterServerApply?: (item: SyncQueueItem) => Promise<void>;
};

/** Ports con prepare/after custom. sale/sale_void ya no tienen port (S2). */
const CUSTOM_PORT_ENTITIES: ReadonlySet<SyncEntity> = new Set(['cash_session', 'product']);

function lazyAdapter(load: () => Promise<FlushRpcAdapter>): FlushRpcAdapter {
  let adapter: FlushRpcAdapter | undefined;
  let pending: Promise<FlushRpcAdapter> | undefined;
  return async (item) => {
    if (!adapter) {
      pending ??= load();
      adapter = await pending;
    }
    return adapter(item);
  };
}

function stampExtractedOperationId(
  entity: SyncEntity,
): CommandPort['prepareOnlinePayload'] {
  return (operation, payload) => {
    const operationId = extractOperationId(entity, operation, payload);
    if (!operationId || payload.operationId === operationId) return payload;
    return { ...payload, operationId };
  };
}

const commandPorts: Partial<Record<SyncEntity, CommandPort>> = {
  cash_session: {
    prepareOnlinePayload: stampExtractedOperationId('cash_session'),
    applyViaRpc: applyCashSessionViaRpc,
    afterServerApply: completeCashSessionAfterServerApply,
  },
  product: {
    prepareOnlinePayload: (operation, payload) => {
      const id =
        (typeof payload.id === 'string' && payload.id.trim()) ||
        (typeof payload.productId === 'string' && payload.productId.trim()) ||
        '';
      return id ? { ...payload, operationId: buildCatalogSyncOperationId(id, payload) } : payload;
    },
    applyViaRpc: lazyAdapter(() =>
      import('../../catalog/infra/productCommandRpc').then((m) => m.applyProductViaRpc),
    ),
  },
};

const ALL_SYNC_ENTITIES: SyncEntity[] = [
  // sale / sale_void: sin port — submitCommand falla cerrado (isSaleInboxEntity).
  'stock_adjustment',
  'stock_transfer',
  'stock_lot',
  'stock_lot_allocation',
  'fiado',
  'product',
  'product_variant',
  'product_catalog_event',
  'promotion',
  'promotion_event',
  'category',
  'cash_session',
  'cash_outflow',
  'cash_outflow_void',
  'expense',
  'expense_void',
  'expense_settlement',
  'expense_settlement_void',
  'expense_attachment',
  'external_service',
  'purchase_order',
  'purchase_order_void',
  'purchase_order_close',
  'purchase_reception',
  'purchase_reception_with_expense',
  'purchase_reception_void',
  'purchase_return',
  'purchase_return_void',
  'branch_transfer',
  'branch_transfer_void',
  'supplier',
  'brand',
  'branch_settings',
  'store_profile',
  'scheduled_closure',
  'kitchen_bump',
  'physical_count_batch',
];

function registerGenericCommandPort(entity: SyncEntity): void {
  if (commandPorts[entity]) return;
  commandPorts[entity] = {
    prepareOnlinePayload: stampExtractedOperationId(entity),
    applyViaRpc: lazyAdapter(() =>
      import('./flushRegistry').then((m) => m.flushItemViaRpc),
    ),
  };
}

export function ensureGenericCommandPort(entity: SyncEntity): void {
  if (!CUSTOM_PORT_ENTITIES.has(entity)) {
    registerGenericCommandPort(entity);
  }
}

for (const entity of ALL_SYNC_ENTITIES) {
  ensureGenericCommandPort(entity);
}

export function getCommandPort(entity: SyncEntity): CommandPort | undefined {
  return commandPorts[entity];
}

export function registerCommandPort(entity: SyncEntity, port: CommandPort): void {
  commandPorts[entity] = port;
}
